// Soft seats (CIN-15126): one shared, rounded unit box that every seat part is an instance of. Instancing
// scales it per part, so the rounding is proportional to the part (a thin armrest gets a thin bevel, a
// cushion a fat one) -- which is what upholstery looks like -- and one geometry serves every seat, style
// and size. The mesh data is plain arrays so the vertex budget is testable without three.js.
// Pure: relative imports only.
import type { SeatRoundness } from './room-design.ts';

/**
 * Corner radius as a fraction of the unit box (0.5 would be a pill). It is applied before the part's own
 * scale, so on a part of size s the visible bevel is `radius * s` along each axis.
 */
export const ROUNDNESS_RADIUS: Record<SeatRoundness, number> = { square: 0, soft: 0.16, round: 0.34 };

/**
 * Triangles a scene of seats may spend in total; beyond it the segments drop, and at the end the box is plain. A
 * normal auditorium keeps the smooth arc, about 1500 seats the single chamfer; bigger rooms (and big rooms on phones)
 * draw plain boxes, which read the same at that distance and keep the frame rate.
 */
export const SEAT_TRIANGLE_BUDGET = { high: 1_200_000, low: 600_000 } as const;
/** Corner subdivisions: 2 gives a smooth arc, 1 a single chamfer. */
const MAX_SEGMENTS = { high: 2, low: 1 } as const;

export type RoundedBoxBuffers = {
    positions: number[];
    normals: number[];
    uvs: number[];
    indices: number[];
};

/** Triangles of the unit box for `segments` corner subdivisions; 0 is the plain 12-triangle box. */
export function roundedBoxTriangles(segments: number): number {
    if (segments < 1) return 12;
    const divisions = 2 * segments + 1;
    return 6 * divisions * divisions * 2;
}

/** Vertices of the unit box for `segments` corner subdivisions (0 is the plain box, four per face). */
export function roundedBoxVertices(segments: number): number {
    const points = segments < 1 ? 2 : 2 * segments + 2;
    return 6 * points * points;
}

/**
 * How many corner subdivisions the seat parts get: as many as the triangle budget allows for
 * `instanceCount` part instances, at most 2 (1 on phones), 0 (a plain box) for `square` or when even one
 * would exceed the budget. Big rooms therefore degrade gracefully instead of stalling.
 */
export function roundingSegments(roundness: SeatRoundness, instanceCount: number, quality: 'low' | 'high'): number {
    if (ROUNDNESS_RADIUS[roundness] <= 0) return 0;
    for (let segments: number = MAX_SEGMENTS[quality]; segments >= 1; segments--) {
        if (instanceCount * roundedBoxTriangles(segments) <= SEAT_TRIANGLE_BUDGET[quality]) return segments;
    }
    return 0;
}

const clamp = (value: number, limit: number) => Math.min(limit, Math.max(-limit, value));

/** Grid stations along one face axis: the first and last `segments` steps are the corner arcs. */
function stations(radius: number, segments: number): number[] {
    if (radius <= 0 || segments < 1) return [-0.5, 0.5];
    const out: number[] = [];
    for (let k = 0; k <= segments; k++) out.push(-0.5 + (radius * k) / segments);
    for (let k = 0; k <= segments; k++) out.push(0.5 - radius + (radius * k) / segments);
    return out;
}

/**
 * The unit box (side 1, centered) with rounded edges and corners: each face is a grid whose outer bands
 * are pulled onto a quarter cylinder or a sphere octant of `radius`. `radius` 0 or `segments` 0 is the plain
 * box. Normals are smooth across the rounded parts and flat on the faces; uv is the face's own 0..1 square.
 */
export function roundedBoxBuffers(radius: number, segments: number): RoundedBoxBuffers {
    const r = Math.min(0.5, Math.max(0, radius));
    const isPlain = r <= 0 || segments < 1;
    const along = stations(r, segments);
    const count = along.length;
    const inner = 0.5 - r;
    const buffers: RoundedBoxBuffers = { positions: [], normals: [], uvs: [], indices: [] };

    for (let axis = 0; axis < 3; axis++) {
        for (const sign of [1, -1]) {
            const u = (axis + 1) % 3;
            const v = (axis + 2) % 3;
            const base = buffers.positions.length / 3;
            for (let i = 0; i < count; i++) {
                for (let j = 0; j < count; j++) {
                    const cube = [0, 0, 0];
                    cube[axis] = sign * 0.5;
                    cube[u] = along[i];
                    cube[v] = along[j];
                    if (isPlain) {
                        const normal = [0, 0, 0];
                        normal[axis] = sign;
                        buffers.positions.push(cube[0], cube[1], cube[2]);
                        buffers.normals.push(normal[0], normal[1], normal[2]);
                    } else {
                        const offset = cube.map((c) => c - clamp(c, inner));
                        const length = Math.hypot(offset[0], offset[1], offset[2]);
                        const normal = offset.map((o) => o / length);
                        buffers.positions.push(
                            clamp(cube[0], inner) + normal[0] * r,
                            clamp(cube[1], inner) + normal[1] * r,
                            clamp(cube[2], inner) + normal[2] * r
                        );
                        buffers.normals.push(normal[0], normal[1], normal[2]);
                    }
                    buffers.uvs.push(along[i] + 0.5, along[j] + 0.5);
                }
            }
            // (u, v, axis) is a right-handed frame, so this winding faces +axis; the negative side flips.
            for (let i = 0; i + 1 < count; i++) {
                for (let j = 0; j + 1 < count; j++) {
                    const a = base + i * count + j;
                    const b = a + count;
                    const c = b + 1;
                    const d = a + 1;
                    if (sign > 0) buffers.indices.push(a, b, c, a, c, d);
                    else buffers.indices.push(a, c, b, a, d, c);
                }
            }
        }
    }
    return buffers;
}
